import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Calendar, ArrowRight, Mail, Clock } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
export const ExitIntentModal = () => {
  const {
    language
  } = useLanguage();
  const [isVisible, setIsVisible] = useState(false);
  const [hasShown, setHasShown] = useState(false);
  const [dismissedAt, setDismissedAt] = useState<number | null>(null);
  const [step, setStep] = useState<'initial' | 'schedule'>('initial');
  // Form state
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedTime, setSelectedTime] = useState('');
  const [message, setMessage] = useState('');
  const translations = {
    en: {
      title: 'Wait! Before you go...',
      subtitle: "I'd love to connect and discuss how I can contribute to your team. Would you be open to a quick 15-minute chat?",
      scheduleButton: 'Schedule a Call',
      noThanks: 'No thanks, maybe later',
      formTitle: 'Schedule a Meeting',
      formSubtitle: 'Fill in your details and pick a time that works for you',
      nameLabel: 'Your Name *',
      namePlaceholder: 'John Doe',
      emailLabel: 'Your Email *',
      emailPlaceholder: 'you@company.com',
      dateLabel: 'Date *',
      timeLabel: 'Time *',
      timeSelect: 'Select',
      messageLabel: 'Message (Optional)',
      messagePlaceholder: 'What would you like to discuss?',
      submitButton: 'Add to Google Calendar',
      disclaimer: 'This will open Google Calendar with pre-filled details',
      back: '← Back'
    },
    vi: {
      title: 'Đợi đã! Trước khi bạn rời đi...',
      subtitle: 'Tôi rất muốn kết nối và thảo luận về cách tôi có thể đóng góp cho đội ngũ của bạn. Bạn có thể dành 15 phút trò chuyện không?',
      scheduleButton: 'Đặt Lịch Gọi',
      noThanks: 'Không, để sau',
      formTitle: 'Đặt Lịch Họp',
      formSubtitle: 'Điền thông tin và chọn thời gian phù hợp với bạn',
      nameLabel: 'Tên của bạn *',
      namePlaceholder: 'Nguyễn Văn A',
      emailLabel: 'Email của bạn *',
      emailPlaceholder: 'ban@congty.com',
      dateLabel: 'Ngày *',
      timeLabel: 'Giờ *',
      timeSelect: 'Chọn',
      messageLabel: 'Tin nhắn (Tùy chọn)',
      messagePlaceholder: 'Bạn muốn thảo luận về điều gì?',
      submitButton: 'Thêm vào Google Calendar',
      disclaimer: 'Điều này sẽ mở Google Calendar với thông tin đã điền sẵn',
      back: '← Quay lại'
    }
  };
  const t = translations[language];
  useEffect(() => {
    const permanentlyDismissed = localStorage.getItem('exit_intent_permanent_dismiss');
    if (permanentlyDismissed) {
      return;
    }
    const hasSeenIntro = localStorage.getItem('hasSeenIntro_v3');
    let timer1: NodeJS.Timeout | null = null;
    let timer2: NodeJS.Timeout | null = null;
    const lastDismissedTime = localStorage.getItem('exit_intent_last_dismissed');
    const initialDelay = lastDismissedTime ? 300000 : 90000;
    timer1 = setTimeout(() => {
      if (!hasShown && !isVisible && hasSeenIntro) {
        setIsVisible(true);
        setHasShown(true);
      }
    }, initialDelay);
    if (!lastDismissedTime) {
      timer2 = setTimeout(() => {
        if (!hasShown && !isVisible && hasSeenIntro) {
          setIsVisible(true);
          setHasShown(true);
        }
      }, 300000);
    }
    const handleMouseLeave = (e: MouseEvent) => {
      if (e.clientY <= 0 && !hasShown && !isVisible && hasSeenIntro) {
        const now = Date.now();
        const pageLoadTime = sessionStorage.getItem('page_load_time');
        const loadTime = pageLoadTime ? parseInt(pageLoadTime) : now;
        const timeOnPage = now - loadTime;
        if (timeOnPage >= 90000) {
          setIsVisible(true);
          setHasShown(true);
        }
      }
    };
    if (!sessionStorage.getItem('page_load_time')) {
      sessionStorage.setItem('page_load_time', Date.now().toString());
    }
    document.addEventListener('mouseleave', handleMouseLeave);
    return () => {
      if (timer1) clearTimeout(timer1);
      if (timer2) clearTimeout(timer2);
      document.removeEventListener('mouseleave', handleMouseLeave);
    };
  }, [hasShown, isVisible]);
  const handleClose = (isDismissal: boolean = false) => {
    setIsVisible(false);
    setStep('initial');
    if (isDismissal) {
      localStorage.setItem('exit_intent_last_dismissed', Date.now().toString());
      setDismissedAt(Date.now());
      setTimeout(() => {
        setHasShown(false);
      }, 1000);
    }
    setEmail('');
    setName('');
    setSelectedDate('');
    setSelectedTime('');
    setMessage('');
  };
  const handleScheduleClick = () => {
    setStep('schedule');
  };
  const handleBackToInitial = () => {
    setStep('initial');
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !name || !selectedDate || !selectedTime) {
      alert('Please fill in all required fields');
      return;
    }
    const dateObj = new Date(selectedDate + 'T' + selectedTime);
    const endDate = new Date(dateObj.getTime() + 30 * 60000);
    const formatGoogleDate = (date: Date) => {
      return date.toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';
    };
    const startTime = formatGoogleDate(dateObj);
    const endTime = formatGoogleDate(endDate);
    // Create Google Calendar URL with proper guest parameter
    const calendarUrl = new URL('https://calendar.google.com/calendar/render');
    calendarUrl.searchParams.append('action', 'TEMPLATE');
    calendarUrl.searchParams.append('text', `Meeting with ${name}`);
    calendarUrl.searchParams.append('dates', `${startTime}/${endTime}`);
    calendarUrl.searchParams.append('details', `Meeting scheduled via portfolio website.\n\nContact: ${email}\n\nMessage: ${message || 'No additional message'}`);
    // Add both emails as guests (the user and you)
    calendarUrl.searchParams.append('add', `${email},officialtrungnguyen@gmail.com`);
    window.open(calendarUrl.toString(), '_blank');
    localStorage.setItem('exit_intent_permanent_dismiss', 'true');
    handleClose(false);
  };
  const generateTimeSlots = () => {
    const slots = [];
    for (let hour = 9; hour <= 17; hour++) {
      for (let minute of [0, 30]) {
        if (hour === 17 && minute === 30) break;
        const time = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
        const displayTime = new Date(`2000-01-01T${time}`).toLocaleTimeString('en-US', {
          hour: 'numeric',
          minute: '2-digit',
          hour12: true
        });
        slots.push({
          value: time,
          label: displayTime
        });
      }
    }
    return slots;
  };
  const timeSlots = generateTimeSlots();
  const getMinDate = () => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  };
  return <AnimatePresence data-id="element-510">
      {isVisible && <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} style={{
      zIndex: 999998
    }} className="fixed inset-0 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 overflow-y-auto" onClick={() => handleClose(false)} data-id="element-511">
          <motion.div initial={{
        scale: 0.9,
        opacity: 0,
        y: 20
      }} animate={{
        scale: 1,
        opacity: 1,
        y: 0
      }} exit={{
        scale: 0.9,
        opacity: 0,
        y: 20
      }} style={{
        zIndex: 999998
      }} className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl w-full max-w-lg my-8 overflow-hidden relative border border-white/20" onClick={e => e.stopPropagation()} data-id="element-512">
            <button onClick={() => handleClose(false)} className="absolute top-4 right-4 p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors z-10" data-id="element-513">
              <X size={20} data-id="element-514" />
            </button>

            <AnimatePresence mode="wait" data-id="element-515">
              {step === 'initial' ? <motion.div key="initial" initial={{
            opacity: 0,
            x: -20
          }} animate={{
            opacity: 1,
            x: 0
          }} exit={{
            opacity: 0,
            x: 20
          }} className="p-8 md:p-10" data-id="element-516">
                  <div className="w-16 h-16 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center mb-6 mx-auto" data-id="element-517">
                    <Calendar className="w-8 h-8 text-blue-600 dark:text-blue-400" data-id="element-518" />
                  </div>

                  <h2 className="text-2xl md:text-3xl font-bold text-center text-gray-900 dark:text-white mb-3" data-id="element-519">
                    {t.title}
                  </h2>

                  <p className="text-center text-gray-600 dark:text-gray-300 mb-8 leading-relaxed" data-id="element-520">
                    {t.subtitle}
                  </p>

                  <div className="flex flex-col gap-3" data-id="element-521">
                    <button onClick={handleScheduleClick} className="w-full py-4 px-6 rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-bold text-lg shadow-lg shadow-blue-500/25 flex items-center justify-center gap-2 transition-all hover:scale-[1.02] active:scale-[0.98]" data-id="element-522">
                      <Calendar size={20} data-id="element-523" />
                      {t.scheduleButton}
                    </button>

                    <button onClick={() => handleClose(true)} className="w-full py-3 px-6 rounded-xl text-gray-500 dark:text-gray-400 font-medium hover:bg-gray-100 dark:hover:bg-slate-800 transition-colors" data-id="element-524">
                      {t.noThanks}
                    </button>
                  </div>
                </motion.div> : <motion.div key="schedule" initial={{
            opacity: 0,
            x: 20
          }} animate={{
            opacity: 1,
            x: 0
          }} exit={{
            opacity: 0,
            x: -20
          }} className="p-8 md:p-10 max-h-[80vh] overflow-y-auto" data-id="element-525">
                  <button onClick={handleBackToInitial} className="text-sm text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 mb-4 flex items-center gap-1" data-id="element-526">
                    {t.back}
                  </button>

                  <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2" data-id="element-527">
                    {t.formTitle}
                  </h2>
                  <p className="text-gray-600 dark:text-gray-300 mb-6 text-sm" data-id="element-528">
                    {t.formSubtitle}
                  </p>

                  <form onSubmit={handleSubmit} className="space-y-4" data-id="element-529">
                    <div data-id="element-530">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-531">
                        {t.nameLabel}
                      </label>
                      <input type="text" value={name} onChange={e => setName(e.target.value)} placeholder={t.namePlaceholder} required className="w-full px-4 py-2.5 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-slate-800 text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all" data-id="element-532" />
                    </div>

                    <div data-id="element-533">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-534">
                        {t.emailLabel}
                      </label>
                      <div className="relative" data-id="element-535">
                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} data-id="element-536" />
                        <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder={t.emailPlaceholder} required className="w-full pl-10 pr-4 py-2.5 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-slate-800 text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all" data-id="element-537" />
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-3" data-id="element-538">
                      <div data-id="element-539">
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-540">
                          {t.dateLabel}
                        </label>
                        <input type="date" value={selectedDate} onChange={e => setSelectedDate(e.target.value)} min={getMinDate()} required className="w-full px-3 py-2.5 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-slate-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all" data-id="element-541" />
                      </div>

                      <div data-id="element-542">
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-543">
                          {t.timeLabel}
                        </label>
                        <div className="relative" data-id="element-544">
                          <Clock className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" size={18} data-id="element-545" />
                          <select value={selectedTime} onChange={e => setSelectedTime(e.target.value)} required className="w-full pl-10 pr-3 py-2.5 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-slate-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all appearance-none" data-id="element-546">
                            <option value="" data-id="element-547">{t.timeSelect}</option>
                            {timeSlots.map(slot => <option key={slot.value} value={slot.value} data-id="element-548">
                                {slot.label}
                              </option>)}
                          </select>
                        </div>
                      </div>
                    </div>

                    <div data-id="element-549">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-550">
                        {t.messageLabel}
                      </label>
                      <textarea value={message} onChange={e => setMessage(e.target.value)} placeholder={t.messagePlaceholder} rows={3} className="w-full px-4 py-2.5 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-slate-800 text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all resize-none" data-id="element-551" />
                    </div>

                    <button type="submit" className="w-full py-3.5 px-6 rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-bold shadow-lg shadow-blue-500/25 flex items-center justify-center gap-2 transition-all hover:scale-[1.02] active:scale-[0.98]" data-id="element-552">
                      {t.submitButton}
                      <ArrowRight size={18} data-id="element-553" />
                    </button>

                    <p className="text-xs text-center text-gray-500 dark:text-gray-400" data-id="element-554">
                      {t.disclaimer}
                    </p>
                  </form>
                </motion.div>}
            </AnimatePresence>
          </motion.div>
        </motion.div>}
    </AnimatePresence>;
};